import { useEffect, useState } from "react";
import { adminFetch, ApiClientError } from "../lib/apiClient";
import { AdminNav } from "../components/admin/AdminNav";

interface SettingsResponse {
  hubspot_portal_id: string;
  hubspot_event_organizer: string;
  slack_webhook_url: string;
  hubspot_token_set: boolean;
}

// テナント別の連携設定（HubSpot / Slack）を入力・保存する管理画面。
// HubSpotトークンは取得時に返らない（設定済みかのみ）。再入力時のみ送信して暗号化保存。
export function AdminSettingsPage() {
  const [portalId, setPortalId] = useState("");
  const [organizer, setOrganizer] = useState("");
  const [slackUrl, setSlackUrl] = useState("");
  const [token, setToken] = useState("");
  const [tokenSet, setTokenSet] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminFetch<SettingsResponse>("/api/admin/settings")
      .then((d) => {
        setPortalId(d.hubspot_portal_id);
        setOrganizer(d.hubspot_event_organizer);
        setSlackUrl(d.slack_webhook_url);
        setTokenSet(d.hubspot_token_set);
      })
      .catch((err) =>
        setError(err instanceof ApiClientError ? err.message : "読み込みに失敗しました"),
      );
  }, []);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body: Record<string, string> = {
        hubspot_portal_id: portalId,
        hubspot_event_organizer: organizer,
        slack_webhook_url: slackUrl,
      };
      // トークンは入力があったときだけ送る（空なら既存を維持）。
      if (token) body.hubspot_private_app_token = token;

      await adminFetch("/api/admin/settings", {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setNotice("保存しました");
      if (token) {
        setTokenSet(true);
        setToken("");
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-page">
      <AdminNav current="settings" />
      <header className="admin-header">
        <div>
          <h1>連携設定</h1>
          <p className="muted">
            このテナントの HubSpot / Slack 連携設定。HubSpotトークンは暗号化して保存されます。
          </p>
        </div>
      </header>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="form-notice">{notice}</p>}

      <form className="settings-form" onSubmit={onSave}>
        <label>
          HubSpot Portal ID
          <input
            type="text"
            value={portalId}
            onChange={(e) => setPortalId(e.target.value)}
            placeholder="例: 6545626"
          />
        </label>

        <label>
          HubSpot Private App Token
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={tokenSet ? "設定済み（変更する場合のみ入力）" : "未設定"}
            autoComplete="new-password"
          />
          <span className="muted">
            {tokenSet ? "現在トークンは設定済みです。" : "トークンは未設定です。"}
            入力した場合のみ上書き保存します。
          </span>
        </label>

        <label>
          HubSpot Marketing Event 主催者名
          <input
            type="text"
            value={organizer}
            onChange={(e) => setOrganizer(e.target.value)}
            placeholder="例: ウェビナー運営"
          />
        </label>

        <label>
          Slack Webhook URL
          <input
            type="text"
            value={slackUrl}
            onChange={(e) => setSlackUrl(e.target.value)}
            placeholder="https://hooks.slack.com/services/..."
          />
        </label>

        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? "保存中…" : "保存"}
        </button>
      </form>
    </div>
  );
}
